<template>
  <div v-if="messages.length" class="form-messages-overlay" data-testid="form-messages-overlay">
    <div
      v-for="message in messages"
      :key="message.id"
      class="form-messages-overlay__balloon"
      :class="`form-messages-overlay__balloon--${message.type}`"
      role="status"
    >
      <pre class="form-messages-overlay__text">{{ message.text }}</pre>
      <div class="form-messages-overlay__actions">
        <button
          v-for="action in message.actions"
          :key="action"
          type="button"
          class="form-messages-overlay__action"
          :class="`form-messages-overlay__action--${action}`"
          :disabled="isMessageBusy(message.id)"
          @click.stop="onChoose(message, action)"
        >
          {{ formMessageActionLabel(action) }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { usePageData } from '@/common/composables/usePageData'
import {
  formMessageActionLabel,
  type FormMessageActionType,
  type FormSoftMessage,
} from '@/services/form-events/formSoftEvent'
import { useFormSoftMessages } from '@/services/form-events/formMessagesStore'
import { executeFormSoftMessageAction } from '@/services/form-events/formEventRouter'

const { pageDataSlot } = usePageData()
const messages = useFormSoftMessages(() => pageDataSlot.value)

/** Пока идёт действие — все кнопки этого message disabled (один выбор). */
const busyMessageIds = ref(new Set<string>())

function isMessageBusy(messageId: string): boolean {
  return busyMessageIds.value.has(messageId)
}

async function onChoose(
  message: FormSoftMessage,
  action: FormMessageActionType,
): Promise<void> {
  if (busyMessageIds.value.has(message.id)) return

  const next = new Set(busyMessageIds.value)
  next.add(message.id)
  busyMessageIds.value = next

  try {
    await executeFormSoftMessageAction(message, action)
  } finally {
    // Сообщение обычно уже снято store; чистим на случай ошибки/скрытия.
    if (busyMessageIds.value.has(message.id)) {
      const cleared = new Set(busyMessageIds.value)
      cleared.delete(message.id)
      busyMessageIds.value = cleared
    }
  }
}
</script>

<style scoped lang="scss">
.form-messages-overlay {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1200;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: min(320px, calc(100% - 16px));
  pointer-events: none;
}

.form-messages-overlay__balloon {
  pointer-events: auto;
  border: 0;
  border-radius: 10px;
  padding: 14px;
  text-align: left;
  box-shadow: 0 4px 12px rgb(0 0 0 / 28%);
  font: inherit;
}

.form-messages-overlay__balloon--alert {
  background: rgb(255, 220, 50);
  color: #111;
}

.form-messages-overlay__balloon--error {
  background: rgb(235, 86, 87);
  color: #fff;
}

.form-messages-overlay__balloon--info {
  background: rgb(65, 65, 65);
  color: #fff;
}

.form-messages-overlay__text {
  margin: 0 0 10px;
  white-space: pre-wrap;
  font-family: inherit;
  font-size: 0.875rem;
  line-height: 1.35;
}

.form-messages-overlay__actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-messages-overlay__action {
  width: 100%;
  border: 1px solid rgb(0 0 0 / 25%);
  border-radius: 6px;
  padding: 0.45rem 0.6rem;
  background: rgb(255 255 255 / 85%);
  color: #111;
  font: inherit;
  font-size: 0.8125rem;
  cursor: pointer;
  text-align: center;
}

.form-messages-overlay__balloon--error .form-messages-overlay__action,
.form-messages-overlay__balloon--info .form-messages-overlay__action {
  background: rgb(255 255 255 / 92%);
}

.form-messages-overlay__action--close_form {
  border-color: rgb(160 40 40 / 45%);
}

.form-messages-overlay__action:hover:not(:disabled) {
  filter: brightness(0.97);
}

.form-messages-overlay__action:active:not(:disabled) {
  filter: brightness(0.93);
}

.form-messages-overlay__action:disabled {
  opacity: 0.55;
  cursor: default;
}
</style>
